import { Check, CircleAlert, ListChecks, X } from 'lucide-react'
import { Button } from '@/components/ui'
import { onboardingSteps, type StepDetail, type StepState } from '../utils/onboarding-steps'

/**
 * Always-visible "where is this request and what do I do next" bar, pinned
 * under the page header (Chris 2026-10-11: the sign-off was "buried in a
 * sidebar you have to scroll to see if you don't have a 5k monitor").
 * Left: the four steps. Right: the one thing this viewer can do now.
 */

export interface BarAction {
  label: string
  icon?: React.ReactNode
  onClick: () => void
  disabled?: boolean
  loading?: boolean
  tone?: 'primary' | 'secondary' | 'ghost' | 'danger'
}

const DOT: Record<StepState, string> = {
  done: 'bg-success text-text-inverse',
  current: 'bg-accent text-text-inverse',
  todo: 'bg-muted text-tertiary',
  failed: 'bg-danger text-text-inverse',
  stopped: 'bg-muted text-tertiary',
}

export function NextStepBar({
  detail,
  primary,
  waiting,
  blocked,
  secondary = [],
  checklist,
}: {
  detail: StepDetail
  /** The one action for this viewer, or null when it isn't their turn. */
  primary: BarAction | null
  /** Shown instead of a button when the viewer can't act ("Waiting for operations"). */
  waiting: string | null
  /** Why the primary action is disabled, if it is. */
  blocked: string | null
  secondary?: BarAction[]
  /** Shown when the side panel isn't on screen (narrower windows). */
  checklist?: { missing: number; onOpen: () => void } | null
}) {
  const steps = onboardingSteps(detail)
  return (
    <section aria-label="Next step" className="border-b border-border-default bg-surface px-6 py-3 shadow-sm">
      {/* One row on wide screens; below 1800px the steps get their own row so they never wrap. */}
      <div className="flex flex-col gap-3 min-[1800px]:flex-row min-[1800px]:items-center min-[1800px]:gap-8">
        <ol className="grid min-w-0 flex-1 grid-cols-4 gap-4">
          {steps.map((step, i) => (
            <li key={step.key} className={`flex items-start gap-2.5 border-t-4 pt-2 ${
              step.state === 'done' ? 'border-success' : step.state === 'current' ? 'border-accent'
                : step.state === 'failed' ? 'border-danger' : 'border-border-default'}`}>
              <span aria-hidden="true"
                className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-sm font-semibold ${DOT[step.state]}`}>
                {step.state === 'done' ? <Check size={16} /> : step.state === 'failed' ? <CircleAlert size={16} />
                  : step.state === 'stopped' ? <X size={14} /> : i + 1}
              </span>
              <span className="min-w-0 leading-tight">
                <span className={`block text-md ${step.state === 'current' ? 'font-semibold text-primary' : 'text-primary'}`}>
                  {step.label}
                  <span className="sr-only">{` (${step.state})`}</span>
                </span>
                {step.detail ? <span className="block truncate text-sm text-secondary">{step.detail}</span> : null}
              </span>
            </li>
          ))}
        </ol>
        <div className="flex flex-wrap items-center justify-end gap-3">
          {checklist ? (
            <Button variant="secondary" onClick={checklist.onOpen}>
              <ListChecks size={18} /> Checklist{checklist.missing ? ` · ${checklist.missing} missing` : ' · complete'}
            </Button>
          ) : null}
          {secondary.map((a) => (
            <Button key={a.label} variant={a.tone ?? 'ghost'} onClick={a.onClick} disabled={a.disabled} loading={a.loading}>
              {a.icon} {a.label}
            </Button>
          ))}
          {primary ? (
            <div className="flex flex-col items-end gap-1">
              <Button variant={primary.tone ?? 'primary'} size="lg" onClick={primary.onClick} disabled={primary.disabled}
                loading={primary.loading}>
                {primary.icon} {primary.label}
              </Button>
              {blocked ? <span className="max-w-80 text-right text-sm text-secondary">{blocked}</span> : null}
            </div>
          ) : waiting ? (
            <span className="rounded-md bg-surface-sunk px-4 py-2 text-md text-secondary">{waiting}</span>
          ) : null}
        </div>
      </div>
    </section>
  )
}
